import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES } from '../constants/languageConfig';

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    border: '1px solid #444',
    borderRadius: '4px',
    background: '#2a2a2a',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
  },
  buttonActive: {
    background: '#4a4a4a',
    borderColor: '#666',
  },
  buttonHover: {
    background: '#3a3a3a',
  },
  flag: {
    fontSize: '18px',
  },
  dropdown: {
    position: 'relative',
    display: 'inline-block',
  },
  select: {
    appearance: 'none',
    padding: '8px 32px 8px 12px',
    border: '1px solid #444',
    borderRadius: '4px',
    background: '#2a2a2a',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '14px',
    minWidth: '140px',
  },
  selectArrow: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#888',
  },
};

/**
 * Language selector component with two display modes:
 * - "buttons": Shows flag buttons for each language (good for 2-3 languages)
 * - "dropdown": Shows a dropdown select (better for 4+ languages)
 */
export function LanguageSelector({ mode = 'buttons' }) {
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [hoveredLang, setHoveredLang] = React.useState(null);

  if (mode === 'dropdown') {
    return (
      <div style={styles.dropdown}>
        <select
          style={styles.select}
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          aria-label="Select language"
        >
          {availableLanguages.map((code) => {
            const lang = SUPPORTED_LANGUAGES[code];
            return (
              <option key={code} value={code}>
                {lang.flag} {lang.name}
              </option>
            );
          })}
        </select>
        <span style={styles.selectArrow}>▼</span>
      </div>
    );
  }

  // Button mode (default)
  return (
    <div style={styles.container} role="group" aria-label="Language selection">
      {availableLanguages.map((code) => {
        const lang = SUPPORTED_LANGUAGES[code];
        const isActive = language === code;
        const isHovered = hoveredLang === code;
        
        return (
          <button
            key={code}
            onClick={() => setLanguage(code)}
            onMouseEnter={() => setHoveredLang(code)}
            onMouseLeave={() => setHoveredLang(null)}
            style={{
              ...styles.button,
              ...(isActive ? styles.buttonActive : {}),
              ...(isHovered && !isActive ? styles.buttonHover : {}),
            }}
            aria-pressed={isActive}
            title={`Switch to ${lang.name}`}
          >
            <span style={styles.flag}>{lang.flag}</span>
            <span>{lang.name}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Compact version using standard button design (for header)
 */
export function LanguageSelectorCompact() {
  const { language, setLanguage, availableLanguages } = useLanguage();

  const compactStyles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    button: {
      background: 'color-mix(in srgb, var(--brand) 40%, transparent)',
      color: 'white',
      border: '1px solid rgba(255,255,255,0.3)',
      padding: '8px 16px',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
    },
    buttonActive: {
      background: 'var(--brand)',
      border: '1px solid var(--brand)',
    },
    buttonHover: {
      background: 'color-mix(in srgb, var(--brand-accent) 50%, transparent)',
    },
  };

  return (
    <div style={compactStyles.container} role="group" aria-label="Language selection">
      {availableLanguages.map((code) => {
        const lang = SUPPORTED_LANGUAGES[code];
        const isActive = language === code;
        
        return (
          <button
            key={code}
            onClick={() => setLanguage(code)}
            style={{
              ...compactStyles.button,
              ...(isActive ? compactStyles.buttonActive : {}),
            }}
            onMouseEnter={(e) => {
              if (!isActive) {
                Object.assign(e.target.style, compactStyles.buttonHover);
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive) {
                Object.assign(e.target.style, compactStyles.button);
              }
            }}
            aria-pressed={isActive}
            title={lang.name}
          >
            {lang.nativeName}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Language badge to show on cards that are displayed in a different language
 */
export function LanguageBadge({ cardLanguage, selectedLanguage, style = {} }) {
  if (!cardLanguage || cardLanguage === selectedLanguage) {
    return null;
  }

  const lang = SUPPORTED_LANGUAGES[cardLanguage];
  if (!lang) {
    return null;
  }

  const badgeStyles = {
    badge: {
      position: 'absolute',
      top: '4px',
      left: '4px',
      padding: '3px 8px',
      background: {
        JP: '#bc002d',       // Japanese red
        'ZH-HANT': '#de2910', // ROC/Taiwan red
        KR: '#003478',       // Korean blue
        EN: '#3c3b6e',       // US blue
      }[cardLanguage] || '#3c3b6e',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 'bold',
      color: 'white',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      zIndex: 10,
      boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
      ...style,
    },
  };

  return (
    <div style={badgeStyles.badge} title={`Showing ${lang.name} version`}>
      {lang.code}
    </div>
  );
}

export default LanguageSelector;
